import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { connect } from "react-redux";
import { get, size, filter, map, merge, debounce, isEmpty } from "lodash";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";

import { fetchPost } from "../../api/fetch";
import CustomInput from "../Common/CustomInput";
import LazyLoadContainer from "../Common/LazyLoadContainer";
import ProfileMatch from "./CorrectConfirmValue/ProfileMatch";
import images from "../../assets/img/images";

const styles = {
  radioContainer: {
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  optionsContainer: {
    maxHeight: 250,
    height: 250,
    overflow: "auto",
  },
  loader: {
    height: 230,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
};

const OverrideProfileSearch = (props) => {
  const {
    classes,
    currentProfile,
    lov,
    selectedProfile,
    setSelectedProfile,
    initialSearchValues,
    profileType,
  } = props;
  const [searchValue, setSearchValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [profilesResults, setProfilesResults] = React.useState();
  const [potentialMatches, setPotentialMatches] = React.useState();
  const [displayedResultsNb, setDisplayedResultsNb] = React.useState(10);

  React.useEffect(() => {
    const fetchPotentialMatches = async () => {
      setLoading(true);
      if (
        size(get(initialSearchValues, ["lpr"])) > 0 ||
        size(get(initialSearchValues, ["ocr"])) > 0
      ) {
        setPotentialMatches(
          await searchMatches(
            null,
            get(initialSearchValues, ["lpr"]),
            get(initialSearchValues, ["ocr"])
          )
        );
      }
      setLoading(false);
    };
    if (!isEmpty(initialSearchValues)) {
      fetchPotentialMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialSearchValues)]);

  const handleChange = (newValue) => {
    setSearchValue(newValue);
    if (size(newValue) > 2) {
      debouncedSearchMatches(newValue);
    } else {
      setProfilesResults(null);
    }
  };

  const selectProfile = (value) => {
    setSelectedProfile(value);
  };

  const clearResults = () => {
    setSearchValue("");
    setProfilesResults(potentialMatches || null);
    setSelectedProfile();
  };

  const debouncedSearchMatches = React.useCallback(
    debounce(async (searchValue) => {
      setLoading(true);
      setProfilesResults(await searchMatches(searchValue));
      setLoading(false);
    }, 500),
    []
  );

  const searchMatches = async (text, lprs, ocrs) => {
    try {
      let body = {
        types: [profileType],
      };
      if (text) {
        body.search_text = text;
      }
      if (lprs) {
        body.lpr = lprs;
      }
      if (ocrs) {
        body.ocr = ocrs;
      }
      let res = await fetchPost("profiles/search", body);
      res = filter(
        res,
        (item) => get(item, ["id"]) !== get(currentProfile, ["id"])
      );
      return res;
    } catch (e) {
      console.log(e);
    }
    return null;
  };

  return (
    <Grid container direction="column">
      <Grid item style={{ marginBottom: "20px" }}>
        <Grid container>
          <Grid item className={classes.radioContainer} />
          <Grid item xs>
            <CustomInput
              name="searchValue"
              value={searchValue}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={I18n.get("site_searchNameOcrLpr")}
              startAdornment={<img src={images.searchIcon} alt="search" />}
              endAdornment={
                searchValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={clearResults}
                      style={{ opacity: "0.54" }}
                      classes={{ root: classes.endAdornment }}
                    >
                      <img
                        src={images.closeIcon}
                        alt="search"
                        width="12px"
                        height="12px"
                      />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <></>
                )
              }
            />
          </Grid>
        </Grid>
      </Grid>
      {loading ? (
        <Grid item className={classes.loader}>
          <CircularProgress color="primary" size={20} />
        </Grid>
      ) : (
        <Grid item className={classes.optionsContainer}>
          <LazyLoadContainer
            loadMore={() => setDisplayedResultsNb(displayedResultsNb + 10)}
            hasMore={
              displayedResultsNb <
              size(merge(profilesResults, potentialMatches))
            }
            containerHeight={250}
          >
            <Grid container direction="column">
              {map(
                filter(
                  merge(profilesResults, potentialMatches),
                  (p, i) => i < displayedResultsNb
                ),
                (p) => (
                  <Grid item key={p.id} style={{ marginBottom: "10px" }}>
                    <Grid container>
                      <Grid item className={classes.radioContainer}>
                        <Radio
                          checked={selectedProfile === p.id}
                          onChange={(e) => selectProfile(e.target.value)}
                          value={p.id}
                          color="primary"
                        />
                      </Grid>
                      <ProfileMatch
                        profile={p}
                        lov={lov}
                        searchValue={searchValue}
                      />
                    </Grid>
                  </Grid>
                )
              )}
              <Grid item>
                <Grid container>
                  <Grid item className={classes.radioContainer}>
                    <Radio
                      checked={selectedProfile === "create"}
                      onChange={(e) => selectProfile(e.target.value)}
                      value="create"
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs className="text-54 flexCenter">
                    {I18n.get("site_noProfileMsg")}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </LazyLoadContainer>
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    potentialMatchesThreshold: get(state, [
      "user",
      "potentialMatchesThreshold",
    ]),
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(OverrideProfileSearch)
);
